import { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { usePrefixedNavigate } from "../hooks/usePrefixedNavigate";
import { useAuth } from "../contexts/AuthContext";
import RoleBasedLayout, {
  type LayoutMenuItem,
} from "../components/RoleBasedLayout";
import {
  LayoutDashboard,
  Building,
  Users,
  ClipboardList,
  FileText,
  BarChart3,
  Settings,
  User,
} from "lucide-react";
import Setting from "./institution/Settings";

import { supabase } from "../integrations/supabase/client";
import Dashboard from "./institution/Dashboard";
import Institutions from "./institution/Institutions";
import Students from "./institution/Students";
import Attendance from "./institution/Attendance";
import Documents from "./institution/Documents";
import Reports from "./institution/Reports";
import Profile from "./institution/Profile";

const institutionAdminMenuItems: LayoutMenuItem[] = [
  {
    icon: LayoutDashboard,
    label: "Dashboard",
    path: "/institution-admin/dashboard",
  },
  {
    icon: Building,
    label: "My Institution",
    path: "/institution-admin/institutions",
  },
  { icon: Users, label: "Students", path: "/institution-admin/students" },
  {
    icon: ClipboardList,
    label: "Attendance",
    path: "/institution-admin/attendance",
  },
  { icon: FileText, label: "Documents", path: "/institution-admin/documents" },
  { icon: BarChart3, label: "Reports", path: "/institution-admin/reports" },
  { icon: Settings, label: "Settings", path: "/institution-admin/settings" },
  { icon: User, label: "Profile", path: "/institution-admin/profile" },
];

export default function InstitutionAdminDashboard() {
  const { user, userRole, loading } = useAuth();
  const navigate = usePrefixedNavigate();
  const [checkingInstitution, setCheckingInstitution] = useState(true);

  useEffect(() => {
    const checkInstitution = async () => {
      if (!user) {
        setCheckingInstitution(false);
        return;
      }

      try {
        const { data: institution } = await supabase
          .from("institutions")
          .select("id")
          .eq("admin_id", user.id)
          .maybeSingle();

        if (!institution) {
          navigate("/institution/onboarding");
        }
      } catch (error) {
        console.error("Error checking institution:", error);
      } finally {
        setCheckingInstitution(false);
      }
    };

    if (!loading && user) {
      checkInstitution();
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!loading && userRole && userRole !== "institution_admin") {
      navigate("/portal-selection");
    }
  }, [userRole, loading, navigate]);

  if (loading || checkingInstitution) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <RoleBasedLayout
      menuItems={institutionAdminMenuItems}
      sidebarTitle="JBF Portal"
      sidebarSubtitle="Institution Admin"
      signOutRedirect="/portal-selection"
      showProfileInDropdown
      profilePath="/institution-admin/profile"
    >
      <Routes>
        <Route
          path="/"
          element={<Navigate to="/institution-admin/institutions" replace />}
        />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/institutions" element={<Institutions />} />
        <Route path="/students" element={<Students />} />
        <Route path="/attendance" element={<Attendance />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/settings" element={<Setting />} />
        <Route path="/profile" element={<Profile />} />
      </Routes>
    </RoleBasedLayout>
  );
}
